import ServiceLayout from "@/components/ServiceLayout";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

export default function Bodenbelag() {
  return (
    <ServiceLayout
      title="Bodenbelag (Laminat, Vinyl) in Augsburg"
      description="Saubere und präzise Verlegung von Laminat und Vinyl für einen langlebigen, stilvollen Boden – passend zugeschnitten und fachgerecht montiert."
      image="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6949609f83fecfaac9e03033.jpg"
      content={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">
            Perfekte Böden für Ihre Räume
          </h2>
          <p className="text-lg text-neutral-600">
            Ein neuer Bodenbelag verändert die Atmosphäre eines Raumes komplett. Wir sind Ihre Experten für die professionelle Verlegung von Laminat und Vinyl in Augsburg. Wir bereiten den Untergrund optimal vor und verlegen Ihren Wunschboden passgenau und fugenfrei.
          </p>
          <p className="text-lg text-neutral-600">
            Laminat und Vinyl bieten eine enorme Vielfalt an Designs und sind gleichzeitig robust und pflegeleicht. Wir beraten Sie gerne bei der Auswahl und sorgen für ein perfektes Verlegeergebnis inklusive passender Sockelleisten und Übergangsprofile.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Fachgerechte Verlegung von Laminat & Vinyl",
              "Vorbereitung des Untergrunds",
              "Montage von Sockelleisten & Profilen",
              "Schnelle und saubere Ausführung"
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-neutral-800 font-medium">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </>
      }
      extendedContent={
        <div className="bg-white">
          <section className="py-16 bg-neutral-50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Professionelle Verlegung von Laminat und Vinyl in Augsburg</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Der Bodenbelag ist das Fundament der Raumgestaltung. Er prägt nicht nur die optische Wirkung eines Zimmers, sondern muss auch im Alltag enormen Belastungen standhalten. Wenn Sie in Augsburg und Umgebung auf der Suche nach einem erfahrenen Handwerker-Team für die Verlegung von Laminat oder Vinyl sind, dann ist Easy Umzug Ihr perfekter Ansprechpartner. Wir bringen das nötige Know-how, das richtige Werkzeug und viel Liebe zum Detail mit, um Ihren Traum vom neuen Fußboden Wirklichkeit werden zu lassen.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Die Wahl zwischen Laminat und Vinyl hängt von Ihren individuellen Bedürfnissen ab. Laminat überzeugt durch seine natürliche Holzoptik und hohe Kratzfestigkeit, was es ideal für Wohn- und Schlafzimmer macht. Vinyl hingegen ist extrem wasserabweisend, fußwarm und trittschalldämmend – die perfekte Wahl für Küchen, Bäder und stark frequentierte Flure. Egal für welchen Belag Sie sich entscheiden, unsere Bodenleger garantieren eine millimetergenaue und fachgerechte Verlegung.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Kostenloses Angebot anfordern
                      </Button>
                    </QuoteModal>
                    <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                      <a href="tel:+4915888640762">
                        <Phone className="w-5 h-5 mr-2" />
                        +49 15888 640762
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 pt-8">
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Vorbereitung ist alles</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Ein perfekter Boden beginnt mit einem perfekten Untergrund. Bevor wir die erste Diele verlegen, prüfen wir den Estrich auf Unebenheiten, Risse und Restfeuchte. Bei Bedarf gleichen wir Höhenunterschiede mit spezieller Ausgleichsmasse an und verlegen eine hochwertige Trittschalldämmung. Nur ein planebener Untergrund verhindert späteres Knarzen oder gar das Brechen der Klick-Verbindungen.
                    </p>
                  </div>
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Das perfekte Finish</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Die Verlegung der Fläche ist nur die halbe Miete. Das wahre Können eines Bodenlegers zeigt sich in den Details. Wir schneiden Heizungsrohre millimetergenau aus, montieren Übergangsprofile zu anderen Räumen exakt auf Höhe und bringen farblich passende Sockelleisten fachmännisch an. Erst wenn auch die letzte Ecke perfekt verkleidet ist, ist unsere Arbeit getan.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Warum Vinyl und Laminat die besten Entscheidungen sind</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Moderne Laminat- und Vinylböden haben ihr Image als billiger Holzersatz längst abgelegt. Dank innovativer Druck- und Prägetechniken sind sie optisch und haptisch kaum noch von echtem Parkett oder Steinboden zu unterscheiden. Sie bieten authentische Holzmaserungen, fühlbare Strukturen und eine unglaubliche Designvielfalt. Gleichzeitig sind sie in der Anschaffung deutlich günstiger und im Alltag viel pflegeleichter als Echtholz.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Ein weiterer großer Vorteil ist die Aufbauhöhe. Besonders bei Renovierungen in Altbauten ist oft nicht viel Platz unter den Türen. Vinylboden ist extrem dünn und lässt sich oft sogar auf bestehenden Fliesen verlegen, ohne dass Türen abgeschliffen werden müssen. Wir beraten Sie gerne vor Ort in Augsburg, welcher Bodenbelag am besten zu Ihren Räumlichkeiten und Ihrem Budget passt.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Jetzt beraten lassen
                      </Button>
                    </QuoteModal>
                  </div>
                </div>

                <div className="bg-primary/5 p-8 border-l-4 border-primary mt-8">
                  <h3 className="font-heading text-2xl font-bold mb-3">Bodenverlegung im Zuge Ihres Umzugs</h3>
                  <p className="text-neutral-700 leading-relaxed">
                    Nutzen Sie die Gelegenheit! Nichts ist ärgerlicher, als einen neuen Boden verlegen zu müssen, wenn die Möbel bereits im Raum stehen. Wenn Sie Ihren Umzug mit Easy Umzug planen, können wir den neuen Laminat- oder Vinylboden in Ihrer neuen Wohnung verlegen, bevor der erste Umzugskarton ankommt. Das spart nicht nur Zeit, sondern auch das lästige Möbelrücken. Ein nahtloser Übergang in Ihr frisch renoviertes Zuhause.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-neutral-950 text-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-3xl font-bold mb-6">Unser Qualitätsversprechen für Ihren Boden</h2>
                <div className="grid sm:grid-cols-3 gap-8 mt-12 text-left">
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Langlebigkeit</h3>
                    <p className="text-neutral-400">Durch die fachgerechte Untergrundvorbereitung und präzise Verlegung garantieren wir eine lange Lebensdauer.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Termintreue</h3>
                    <p className="text-neutral-400">Wir arbeiten zügig und halten uns strikt an den vereinbarten Zeitplan für Ihre Renovierung.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Saubere Arbeit</h3>
                    <p className="text-neutral-400">Wir hinterlassen die Baustelle besenrein und nehmen anfallenden Verschnitt direkt mit.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      }
    />
  );
}
